import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import customTripService from '../services/customTripService'
import Loader from '../components/Loader'
import {
  FaMapMarkerAlt,
  FaCar,
  FaCalendar,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
} from 'react-icons/fa'
import SEO from '../utils/SEO'
import Button from '../components/ui/Button'

const MyCustomTrips = () => {
  const [trips, setTrips] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedTrip, setSelectedTrip] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)

  useEffect(() => {
    loadMyTrips()
  }, [])

  const loadMyTrips = async () => {
    try {
      setLoading(true)
      const data = await customTripService.getMyCustomTrips()
      setTrips(data.trips || [])
    } catch (error) {
      console.error('Error loading my trips:', error)
      toast.error('Error loading your trips')
    } finally {
      setLoading(false)
    }
  }

  const viewDetails = async (trip) => {
    setSelectedTrip(trip)
    setShowDetailModal(true)

    // Mark as viewed (this will also happen on the backend when fetching by ID)
    try {
      await customTripService.markAsViewedByUser(trip.id)
      // Reload trips to update the viewed status
      loadMyTrips()
    } catch (error) {
      console.error('Error marking trip as viewed:', error)
    }
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        icon: <FaClock className="inline mr-1" />,
      },
      approved: {
        color: 'bg-green-100 text-green-800 border-green-300',
        icon: <FaCheckCircle className="inline mr-1" />,
      },
      rejected: {
        color: 'bg-red-100 text-red-800 border-red-300',
        icon: <FaTimesCircle className="inline mr-1" />,
      },
      completed: {
        color: 'bg-blue-100 text-blue-800 border-blue-300',
        icon: <FaCheckCircle className="inline mr-1" />,
      },
    }

    const config = statusConfig[status] || statusConfig.pending

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${config.color}`}>
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  const isNewUpdate = (trip) => {
    // Show "new" badge if status is not pending and user hasn't viewed it
    return (
      trip.status !== 'pending' &&
      (!trip.user_viewed_at || new Date(trip.user_viewed_at) < new Date(trip.updated_at))
    )
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader size={80} />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <SEO title="My Custom Trips" noindex />
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">My Custom Trip Requests</h1>
        <p className="text-gray-600 mt-2">View the status of your custom trip planning requests</p>
      </div>

      {trips.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-gray-500 text-lg">You haven't created any custom trip requests yet.</p>
          <Button to="/tour-planner" className="mt-4">
            Create Custom Trip
          </Button>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {trips.map((trip) => (
            <div
              key={trip.id}
              className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow relative"
            >
              {isNewUpdate(trip) && (
                <div className="absolute top-2 right-2 z-10">
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                    NEW UPDATE
                  </span>
                </div>
              )}

              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{trip.trip_name}</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{trip.trip_description}</p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-gray-600 text-sm">
                    <FaMapMarkerAlt className="mr-2 text-primary-600" />
                    <span>{trip.location_count || 0} locations</span>
                  </div>
                  <div className="flex items-center text-gray-600 text-sm">
                    <FaCar className="mr-2 text-primary-600" />
                    <span>
                      {trip.total_distance
                        ? `${parseFloat(trip.total_distance).toFixed(1)} km`
                        : '0 km'}
                    </span>
                  </div>
                  <div className="flex items-center text-gray-600 text-sm">
                    <FaCalendar className="mr-2 text-primary-600" />
                    <span>{new Date(trip.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="mb-4">{getStatusBadge(trip.status)}</div>

                {trip.admin_notes && trip.status !== 'pending' && (
                  <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
                    <p className="text-sm text-gray-700 font-semibold mb-1">Admin Notes:</p>
                    <p className="text-sm text-gray-600">{trip.admin_notes}</p>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <p className="text-lg font-bold text-primary-600">
                    ₹{trip.total_cost ? parseFloat(trip.total_cost).toLocaleString() : '0'}
                  </p>
                  <Button onClick={() => viewDetails(trip)} size="sm">
                    View Details
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedTrip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">{selectedTrip.trip_name}</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Status</h3>
                {getStatusBadge(selectedTrip.status)}
                {selectedTrip.admin_notes && (
                  <div className="mt-3 p-4 bg-gray-50 rounded border border-gray-200">
                    <p className="text-sm font-semibold text-gray-700 mb-1">Admin Notes:</p>
                    <p className="text-sm text-gray-600">{selectedTrip.admin_notes}</p>
                  </div>
                )}
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Trip Details</h3>
                <p className="text-gray-600 mb-4">{selectedTrip.trip_description}</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Total Distance</p>
                    <p className="text-lg font-semibold text-gray-800">
                      {selectedTrip.total_distance
                        ? `${parseFloat(selectedTrip.total_distance).toFixed(1)} km`
                        : '0 km'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Cost</p>
                    <p className="text-lg font-semibold text-primary-600">
                      ₹
                      {selectedTrip.total_cost
                        ? parseFloat(selectedTrip.total_cost).toLocaleString()
                        : '0'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Car</p>
                    <p className="text-lg font-semibold text-gray-800">
                      {selectedTrip.car_name || 'Not selected'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Requested On</p>
                    <p className="text-lg font-semibold text-gray-800">
                      {new Date(selectedTrip.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MyCustomTrips
